import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {addYears} from 'date-fns'
import {Box, Button, useSlotRecipe} from '@chakra-ui/react3'
import InputField from '../input-field'
import Recaptcha from '../../../components/recaptcha'
import useGlobalFields from '../../../components/forms/useGlobalFields'
import {LAYOUT_FIELD_PROPS_MAP, newsletterFormSlotRecipe} from './index.recipe'

/**
 * One-step newsletter sign-up form: optional due date, email, marketing consent.
 *
 * Shared by the newsletter CMS module and the footer newsletter banner. Form state and submission
 * are owned by the parent newsletter flow.
 *
 * @param {Object} props
 * @param {Object} props.form - React Hook Form instance.
 * @param {Function} props.submitForm - Newsletter submit handler.
 * @param {Object} props.recaptchaRef - Ref passed to reCAPTCHA.
 * @param {'outline'|'filled'} [props.variant='filled'] - Style of the due date, email and consent fields.
 * @param {'default'|'condensed'} [props.layout='default'] - Form layout: labels above the fields, or
 * placeholders only with tighter spacing.
 * @returns {React.ReactElement}
 */
const NewsletterForm = ({
    form,
    submitForm,
    recaptchaRef,
    variant = 'filled',
    layout = 'default'
}) => {
    const recipe = useSlotRecipe({recipe: newsletterFormSlotRecipe})
    const styles = useMemo(() => recipe({layout}), [recipe, layout])
    const fieldProps = LAYOUT_FIELD_PROPS_MAP[layout]
    const {formatMessage} = useIntl()
    const {control, handleSubmit, formState} = form
    const {email: emailField} = useGlobalFields({form})

    // a due date lies between today and one year ahead
    const dueDateRange = useMemo(() => {
        const today = new Date()
        return {minDate: today, maxDate: addYears(today, 1)}
    }, [])

    const dueDateLabel = formatMessage({
        id: 'leap_newsletter.label.due_date',
        defaultMessage: "Baby's Due Date (optional)"
    })

    return (
        <Box as="form" css={styles.form} onSubmit={handleSubmit(submitForm)} noValidate>
            <Box css={styles.fields}>
                <InputField
                    name="pregnantDueDate"
                    type="date"
                    control={control}
                    label={dueDateLabel}
                    placeholder={dueDateLabel}
                    variant={variant}
                    {...fieldProps}
                    {...dueDateRange}
                />
                <Box css={styles.emailGroup}>
                    <InputField
                        {...emailField}
                        placeholder={formatMessage({id: 'global.email', defaultMessage: 'Email'})}
                        {...fieldProps}
                        variant={variant}
                        required
                    />
                    <Recaptcha ref={recaptchaRef} display="text" />
                </Box>
                <Box css={styles.consent}>
                    <InputField
                        name="optIn"
                        type="checkbox"
                        variant={variant}
                        control={control}
                        required
                        label={formatMessage({
                            id: 'leap_newsletter.label.consent',
                            defaultMessage:
                                "Yes, I agree to receive personalised marketing emails from Stokke based on the information I provide (including my child's birthdate), my profile data, purchase history and activity in Stokke's digital channels."
                        })}
                        rules={{
                            validate: (checked) =>
                                checked ||
                                formatMessage({
                                    id: 'leap_newsletter.error.consent_required',
                                    defaultMessage: 'Please agree to receive marketing emails.'
                                })
                        }}
                    />
                </Box>
            </Box>
            <Button
                type="submit"
                variant="primaryFilled"
                css={styles.submit}
                loading={formState.isSubmitting}
            >
                <span>
                    <FormattedMessage
                        id="leap_newsletter.button.subscribe"
                        defaultMessage="Subscribe"
                    />
                </span>
            </Button>
        </Box>
    )
}

NewsletterForm.propTypes = {
    form: PropTypes.object.isRequired,
    submitForm: PropTypes.func.isRequired,
    recaptchaRef: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(['outline', 'filled']),
    layout: PropTypes.oneOf(['default', 'condensed'])
}

export default NewsletterForm
